import { DatePipe } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../../../environments/environment';
import { formatMoneyAmount } from '../../../price-field.utils';
import {
  BoardField,
  CardTimelineAuthor,
  CardTimelineCardUpdateXData,
  CardTimelineChatUpdateXData,
  CardTimelineTaskUpdateXData,
  CardTimelineCompanyUpdateXData,
  CardTimelineContactUpdateXData,
  CardTimelineEventAction,
  CardTimelineEventSubType,
  CardTimelineEventType,
  CardTimelineLinkedCompany,
  CardTimelineLinkedContact,
  CardTimelineMergedCard,
  CardTimelineOtherEvent,
  CardTimelineStage,
} from '../../../board-v3.types';
import { formatFullName } from '../../../../../helpers';
import { getContactName } from '../../../contacts/contact.utils';
import { AddressInput } from '../../../../../interfaces/card';
import { formatAddress, getAddressIdentity } from '../../../address.utils';
import { BoardContactService } from '../../../../../services/board-contact.service';
import { BoardCompanyService } from '../../../../../services/board-company.service';
import type { ContactDetailResult } from '../../../contacts/contact-detail/contact-detail.component';
import type { CompanyDetailResult } from '../../../companies/company-detail/company-detail.component';

type TimelineDiffStatus = 'added' | 'removed' | 'changed';

export interface TimelineContactChange {
  contactId: string;
  result: ContactDetailResult;
}

export interface TimelineCompanyChange {
  companyV2Id: string;
  result: CompanyDetailResult;
}

interface TimelineCardLink {
  linked: boolean;
  card: CardTimelineMergedCard;
}

interface TimelineContactLink {
  linked: boolean;
  contact: CardTimelineLinkedContact;
  name: string;
}

/** Company of the card before and after the event: only `new` is a link, only `old` an unlink, both a replace */
interface TimelineCompanyLink {
  old: CardTimelineLinkedCompany | null;
  new: CardTimelineLinkedCompany | null;
}

interface TimelineDiffRow {
  label: string;
  translateLabel: boolean;
  status: TimelineDiffStatus;
  old: string | null;
  new: string | null;
  oldAvatarUrl?: string | null;
  newAvatarUrl?: string | null;
  /** The values are people: the avatar is shown even without a photo (initials) */
  isPerson?: boolean;
  oldColor?: string | null;
  newColor?: string | null;
}

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const LIGHT_LUMINANCE_THRESHOLD = 0.85;
const DARK_FALLBACK = '#374151';

@Component({
  selector: 'app-board-v3-timeline-update',
  templateUrl: './timeline-update.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3TimelineUpdateComponent {
  @Input() event!: CardTimelineOtherEvent;
  @Input() fieldDefinitions?: Map<string, BoardField>;
  @Input() cardCurrency?: string | null;
  /** Contact opened from the event was changed, unlinked or deleted */
  @Output() contactChanged = new EventEmitter<TimelineContactChange>();
  /** Company opened from the event was changed, unlinked or deleted */
  @Output() companyChanged = new EventEmitter<TimelineCompanyChange>();

  private readonly datePipe = inject(DatePipe);
  private readonly translateService = inject(TranslateService);
  private readonly contactService = inject(BoardContactService);
  private readonly companyService = inject(BoardCompanyService);
  private readonly destroyRef = inject(DestroyRef);

  readonly CardTimelineEventType = CardTimelineEventType;
  readonly CardTimelineEventAction = CardTimelineEventAction;

  get isMerge(): boolean {
    return this.event.sub_type === CardTimelineEventSubType.MERGE;
  }

  get mergedCards(): CardTimelineMergedCard[] {
    if (!this.isMerge) return [];

    return (this.event.x_data as CardTimelineCardUpdateXData).merge?.merged_cards ?? [];
  }

  get isContact(): boolean {
    return this.event.event_type === CardTimelineEventType.CONTACT;
  }

  /** Contact linked to or unlinked from the card; create / delete events carry a snapshot under the same key */
  get contactLink(): TimelineContactLink | null {
    if (!this.isContact || this.event.action !== CardTimelineEventAction.UPDATE) return null;

    const change = (this.event.x_data as CardTimelineContactUpdateXData).contact;
    const contact = change?.new ?? change?.old;
    if (!contact) return null;

    return { linked: !!change?.new, contact, name: getContactName(contact) };
  }

  openContact(contact: CardTimelineLinkedContact): void {
    this.contactService
      .openContactDetail(contact.id, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.contact || result?.deleted) this.contactChanged.emit({ contactId: contact.id, result });
      });
  }

  get isCompany(): boolean {
    return this.event.event_type === CardTimelineEventType.COMPANY;
  }

  /** Company linked to, unlinked from or replaced on the card; create / delete events carry a snapshot under the same key */
  get companyLink(): TimelineCompanyLink | null {
    if (!this.isCompany || this.event.action !== CardTimelineEventAction.UPDATE) return null;

    const change = (this.event.x_data as CardTimelineCompanyUpdateXData).company;
    if (!change?.old && !change?.new) return null;

    return { old: change.old ?? null, new: change.new ?? null };
  }

  openCompany(company: CardTimelineLinkedCompany): void {
    this.companyService
      .openCompanyDetail(company.id, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.company || result?.deleted) this.companyChanged.emit({ companyV2Id: company.id, result });
      });
  }

  get isChat(): boolean {
    return this.event.event_type === CardTimelineEventType.CHAT;
  }

  private get chatData(): CardTimelineChatUpdateXData {
    return this.event.x_data as CardTimelineChatUpdateXData;
  }

  get isTask(): boolean {
    return this.event.event_type === CardTimelineEventType.TASK;
  }

  private get taskData(): CardTimelineTaskUpdateXData {
    return this.event.x_data as CardTimelineTaskUpdateXData;
  }

  /** Card linked to or unlinked from the contact or the company */
  get cardLink(): TimelineCardLink | null {
    if (!this.isContact && !this.isCompany) return null;

    const change = (this.event.x_data as CardTimelineContactUpdateXData | CardTimelineCompanyUpdateXData).card;
    const card = change?.new ?? change?.old;
    return card ? { linked: !!change?.new, card } : null;
  }

  get icon(): string {
    if (this.isMerge) return 'call_merge';
    if (this.contactLink) return this.contactLink.linked ? 'link' : 'link_off';
    if (this.companyLink) {
      if (this.companyLink.old && this.companyLink.new) return 'swap_horiz';
      return this.companyLink.new ? 'link' : 'link_off';
    }
    if (this.cardLink) return this.cardLink.linked ? 'link' : 'link_off';
    if (this.isContact) {
      if (this.event.action === CardTimelineEventAction.CREATE) return 'person_add';
      if (this.event.action === CardTimelineEventAction.DELETE) return 'person_remove';
      return 'edit';
    }
    if (this.isCompany) {
      if (this.event.action === CardTimelineEventAction.CREATE) return 'domain_add';
      if (this.event.action === CardTimelineEventAction.DELETE) return 'domain_disabled';
      return 'edit';
    }
    if (this.isChat && this.chatData.manager) return 'support_agent';
    if (this.isTask) {
      if (this.taskData.status) return 'swap_horiz';
      if (this.taskData.deadline) return 'event';
      return this.event.action === CardTimelineEventAction.CREATE ? 'add_task' : 'edit';
    }
    if (this.event.event_type === CardTimelineEventType.SYSTEM) return 'info';
    if (this.event.action === CardTimelineEventAction.CREATE) return 'add_circle';
    if (this.event.action === CardTimelineEventAction.DELETE) return 'delete';

    return 'edit';
  }

  get titleKey(): string {
    if (this.isMerge) return 'timeline.cards_merged';
    if (this.contactLink) return this.contactLink.linked ? 'timeline.contact_linked' : 'timeline.contact_unlinked';
    if (this.companyLink) {
      if (this.companyLink.old && this.companyLink.new) return 'timeline.company_changed';
      return this.companyLink.new ? 'timeline.company_linked' : 'timeline.company_unlinked';
    }
    if (this.cardLink) return this.cardLink.linked ? 'timeline.card_linked' : 'timeline.card_unlinked';
    if (this.isContact) {
      if (this.event.action === CardTimelineEventAction.CREATE) return 'timeline.contact_created';
      if (this.event.action === CardTimelineEventAction.DELETE) return 'timeline.contact_deleted';
      return 'timeline.contact_updated';
    }
    if (this.isCompany) {
      if (this.event.action === CardTimelineEventAction.CREATE) return 'timeline.company_created';
      if (this.event.action === CardTimelineEventAction.DELETE) return 'timeline.company_deleted';
      return 'timeline.company_updated';
    }
    if (this.isChat) {
      const manager = this.chatData.manager;
      if (!manager) return 'timeline.chat_title_changed';
      if (!manager.old) return 'timeline.manager_assigned';
      return manager.new ? 'timeline.manager_changed' : 'timeline.manager_unassigned';
    }
    if (this.isTask) {
      if (this.taskData.status) return 'crm_tasks.timeline.status_changed';
      if (this.taskData.deadline) return 'crm_tasks.timeline.deadline_changed';
      return this.event.action === CardTimelineEventAction.CREATE ? 'crm_tasks.timeline.created' : 'crm_tasks.timeline.updated';
    }
    if (this.event.event_type === CardTimelineEventType.SYSTEM) return 'timeline.system_event';
    if (this.event.action === CardTimelineEventAction.CREATE) return 'timeline.card_created';
    if (this.event.action === CardTimelineEventAction.DELETE) return 'timeline.card_deleted';

    return 'timeline.card_updated';
  }

  get diffRows(): TimelineDiffRow[] {
    if (this.event.action !== CardTimelineEventAction.UPDATE) return [];
    if (this.isContact) return this.getContactDiffRows();
    if (this.isCompany) return this.getCompanyDiffRows();
    if (this.isChat) return this.getChatDiffRows();
    if (this.isTask) return this.getTaskDiffRows();
    if (this.event.event_type !== CardTimelineEventType.CARD) return [];

    const data = this.event.x_data as CardTimelineCardUpdateXData;
    const rows: TimelineDiffRow[] = [];

    for (const key of Object.keys(data) as (keyof CardTimelineCardUpdateXData)[]) {
      switch (key) {
        case 'name': {
          const name = data.name!;
          rows.push(this.buildRow('name', true, name.old, name.new));
          break;
        }
        case 'stage': {
          const stage = data.stage!;
          const row = this.buildRow('timeline_field_stage', true, stage.old?.name ?? null, stage.new?.name ?? null);
          row.oldColor = this.getStageAccentColor(stage.old);
          row.newColor = this.getStageAccentColor(stage.new);
          rows.push(row);
          break;
        }
        case 'assigned': {
          const assigned = data.assigned!;
          const row = this.buildRow(
            'timeline_field_manager',
            true,
            this.getPersonName(assigned.old),
            this.getPersonName(assigned.new),
          );
          row.oldAvatarUrl = this.getPersonAvatarUrl(assigned.old);
          row.newAvatarUrl = this.getPersonAvatarUrl(assigned.new);
          row.isPerson = true;
          rows.push(row);
          break;
        }
        case 'price': {
          const price = data.price!;
          const currencyOld = data.currency?.old ?? this.cardCurrency ?? undefined;
          const currencyNew = data.currency?.new ?? this.cardCurrency ?? undefined;
          rows.push(
            this.buildRow(
              'timeline_field_price',
              true,
              price.old !== null && price.old !== undefined ? formatMoneyAmount(price.old, currencyOld) : null,
              price.new !== null && price.new !== undefined ? formatMoneyAmount(price.new, currencyNew) : null,
            ),
          );
          break;
        }
        case 'currency': {
          if (data.price) break;
          const currency = data.currency!;
          rows.push(this.buildRow('timeline_field_currency', true, currency.old, currency.new));
          break;
        }
        case 'phones': {
          const row = this.buildListDiffRow('timeline_field_phones', data.phones?.old ?? [], data.phones?.new ?? []);
          if (row) rows.push(row);
          break;
        }
        case 'emails': {
          const row = this.buildListDiffRow('timeline_field_emails', data.emails?.old ?? [], data.emails?.new ?? []);
          if (row) rows.push(row);
          break;
        }
        case 'addresses': {
          const row = this.buildAddressDiffRow(data.addresses?.old ?? [], data.addresses?.new ?? []);
          if (row) rows.push(row);
          break;
        }
        case 'description': {
          const description = data.description!;
          rows.push(this.buildRow('timeline_field_description', true, description.old, description.new));
          break;
        }
        case 'custom_fields':
        case 'merge':
          break;
        default: {
          const value = data[key] as { old?: unknown; new?: unknown } | undefined;
          rows.push(this.buildRow(key, false, this.formatValue(value?.old), this.formatValue(value?.new)));
          break;
        }
      }
    }

    return rows;
  }

  get customFieldDiffRows(): TimelineDiffRow[] {
    if (this.event.event_type !== CardTimelineEventType.CARD || this.event.action !== CardTimelineEventAction.UPDATE)
      return [];

    const data = this.event.x_data as CardTimelineCardUpdateXData;

    return Object.entries(data.custom_fields ?? {}).map(([identifier, diff]) => {
      const field = this.fieldDefinitions?.get(identifier);
      const label = field?.name ?? identifier;

      if (field?.input_type === 'checkbox') {
        return this.buildCheckboxRow(label, diff.old, diff.new);
      }

      return this.buildRow(label, false, this.formatValue(diff.old), this.formatValue(diff.new));
    });
  }

  private getChatDiffRows(): TimelineDiffRow[] {
    const { title, manager } = this.chatData;
    const rows: TimelineDiffRow[] = [];

    if (title) rows.push(this.buildRow('timeline_field_chat_title', true, title.old, title.new));
    if (manager) {
      const row = this.buildRow('timeline_field_manager', true, this.getPersonName(manager.old), this.getPersonName(manager.new));
      row.oldAvatarUrl = this.getPersonAvatarUrl(manager.old);
      row.newAvatarUrl = this.getPersonAvatarUrl(manager.new);
      row.isPerson = true;
      rows.push(row);
    }

    return rows;
  }

  private getTaskDiffRows(): TimelineDiffRow[] {
    const { status, deadline } = this.taskData;
    const rows: TimelineDiffRow[] = [];
    const statusLabel = (value: string | null) => (value ? this.translateService.instant(`crm_tasks.status.${value}`) : null);
    const date = (value: string | null) => (value ? this.datePipe.transform(value, 'dd.MM.yyyy HH:mm') : null);

    if (status) rows.push(this.buildRow('crm_tasks.fields.status', true, statusLabel(status.old), statusLabel(status.new)));
    if (deadline) rows.push(this.buildRow('crm_tasks.fields.deadline', true, date(deadline.old), date(deadline.new)));

    return rows;
  }

  private getContactDiffRows(): TimelineDiffRow[] {
    const data = this.event.x_data as CardTimelineContactUpdateXData;
    const rows: TimelineDiffRow[] = [];

    for (const key of Object.keys(data) as (keyof CardTimelineContactUpdateXData)[]) {
      switch (key) {
        case 'contact':
        case 'card':
          break;
        case 'first_name':
        case 'last_name':
        case 'middle_name': {
          const value = data[key]!;
          rows.push(this.buildRow(key, true, value.old, value.new));
          break;
        }
        case 'phones': {
          const row = this.buildListDiffRow('timeline_field_phones', data.phones?.old ?? [], data.phones?.new ?? []);
          if (row) rows.push(row);
          break;
        }
        case 'emails': {
          const row = this.buildListDiffRow('timeline_field_emails', data.emails?.old ?? [], data.emails?.new ?? []);
          if (row) rows.push(row);
          break;
        }
        case 'addresses': {
          const row = this.buildAddressDiffRow(data.addresses?.old ?? [], data.addresses?.new ?? []);
          if (row) rows.push(row);
          break;
        }
        default: {
          const value = data[key] as { old?: unknown; new?: unknown } | undefined;
          rows.push(this.buildRow(key, false, this.formatValue(value?.old), this.formatValue(value?.new)));
          break;
        }
      }
    }

    return rows;
  }

  private getCompanyDiffRows(): TimelineDiffRow[] {
    const data = this.event.x_data as CardTimelineCompanyUpdateXData;
    const rows: TimelineDiffRow[] = [];

    for (const key of Object.keys(data) as (keyof CardTimelineCompanyUpdateXData)[]) {
      switch (key) {
        case 'company':
        case 'card':
          break;
        case 'name': {
          const name = data.name!;
          rows.push(this.buildRow('board_v3_company_name', true, name.old, name.new));
          break;
        }
        case 'phones': {
          const row = this.buildListDiffRow('timeline_field_phones', data.phones?.old ?? [], data.phones?.new ?? []);
          if (row) rows.push(row);
          break;
        }
        case 'emails': {
          const row = this.buildListDiffRow('timeline_field_emails', data.emails?.old ?? [], data.emails?.new ?? []);
          if (row) rows.push(row);
          break;
        }
        case 'addresses': {
          const row = this.buildAddressDiffRow(data.addresses?.old ?? [], data.addresses?.new ?? []);
          if (row) rows.push(row);
          break;
        }
        default: {
          const value = data[key] as { old?: unknown; new?: unknown } | undefined;
          rows.push(this.buildRow(key, false, this.formatValue(value?.old), this.formatValue(value?.new)));
          break;
        }
      }
    }

    return rows;
  }

  getTimelineAuthorName(): string {
    const user = this.event?.created_by_user;
    if (!user) return 'System';

    return formatFullName(user.first_name, user.last_name) || user.email || 'System';
  }

  getTimelineAuthorAvatarUrl(): string | undefined {
    return this.event?.created_by_user?.avatar_url || undefined;
  }

  private buildRow(
    label: string,
    translateLabel: boolean,
    oldValue: string | null,
    newValue: string | null,
  ): TimelineDiffRow {
    const status: TimelineDiffStatus = !oldValue && newValue ? 'added' : oldValue && !newValue ? 'removed' : 'changed';

    return { label, translateLabel, status, old: oldValue, new: newValue };
  }

  private buildCheckboxRow(label: string, oldValue: unknown, newValue: unknown): TimelineDiffRow {
    return {
      label,
      translateLabel: false,
      status: 'changed',
      old: this.translateService.instant(oldValue ? 'yes' : 'no'),
      new: this.translateService.instant(newValue ? 'yes' : 'no'),
    };
  }

  private buildListDiffRow(label: string, oldList: string[], newList: string[]): TimelineDiffRow | null {
    const added = newList.filter((value) => !oldList.includes(value));
    const removed = oldList.filter((value) => !newList.includes(value));

    if (!added.length && !removed.length) return null;

    return this.buildRow(label, true, removed.join(', ') || null, added.join(', ') || null);
  }

  /** Addresses contain commas, so the lists are joined with `;` */
  private buildAddressDiffRow(oldList: AddressInput[], newList: AddressInput[]): TimelineDiffRow | null {
    const oldIdentities = new Set(oldList.map(getAddressIdentity));
    const newIdentities = new Set(newList.map(getAddressIdentity));
    const apartmentLabel = String(this.translateService.instant('address_apartment_short'));
    const format = (list: AddressInput[]) => list.map((address) => formatAddress(address, apartmentLabel)).join('; ');
    const added = newList.filter((address) => !oldIdentities.has(getAddressIdentity(address)));
    const removed = oldList.filter((address) => !newIdentities.has(getAddressIdentity(address)));

    if (!added.length && !removed.length) return null;

    return this.buildRow('timeline_field_addresses', true, format(removed) || null, format(added) || null);
  }

  private getPersonName(
    person: { first_name?: string | null; last_name?: string | null; email?: string | null } | null | undefined,
  ): string | null {
    if (!person) return null;

    return formatFullName(person.first_name, person.last_name) || person.email || null;
  }

  private getStageAccentColor(stage: CardTimelineStage | null | undefined): string | null {
    const background = stage?.color?.background;
    if (!background || typeof background !== 'string') return null;

    const trimmed = background.trim();
    if (!HEX_COLOR_PATTERN.test(trimmed)) return null;

    return this.isLightColor(trimmed) ? DARK_FALLBACK : trimmed;
  }

  private isLightColor(hex: string): boolean {
    const h = hex.replace('#', '');
    if (h.length !== 6) return false;

    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > LIGHT_LUMINANCE_THRESHOLD;
  }

  private getPersonAvatarUrl(person: CardTimelineAuthor | null | undefined): string | undefined {
    return person?.avatar_url || undefined;
  }

  private formatValue(value: unknown): string | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string' && this.isIsoDate(value))
      return this.datePipe.transform(value, 'dd MMM yyyy HH:mm') ?? value;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);

    return JSON.stringify(value);
  }

  private isIsoDate(value: string): boolean {
    return (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/.test(value) &&
      !Number.isNaN(Date.parse(value))
    );
  }
}
